import React, { useState, useEffect, useRef, useCallback, useMemo, useDeferredValue } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { 
  Briefcase, ArrowRight, Upload, Check, AlertCircle, Users, 
  Sparkles, Rocket, Target, Zap, Code, Brain, Shield, 
  TrendingUp, Award, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Search, MapPin, Clock,
  GraduationCap, Building2, CheckCircle2, UserCheck, Layers, X
} from 'lucide-react';
import { careersContent, companyInfo } from '../data/mock';
import { openPositionsData, experienceLevelOptions } from '../data/openPositionsData';
import axios from 'axios';
import { 
  calculateCtcIncreasePercentage, 
  getCompensationReviewScenario 
} from '../utils/compensationReview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Icon mapping per department
const departmentIcons = {
  "Office of the CEO": UserCheck,
  "Finance, Treasury & Corporate Governance Division": Shield,
  "Human Capital Division": Users,
  "Global Market Intelligence & Strategic Research Division": Target,
  "Quantitative Research & Strategy Development Division": Brain,
  "AI Engineering, Data Science & Automation Division": Code,
  "Enterprise Risk Management Division": Award,
  "Investment Operations & Trade Execution Division": TrendingUp,
  "Technology Infrastructure & Cybersecurity Division": Zap
};

// Module-level static arrays to avoid inline allocations
const WHY_WORK_PERKS = [
  { icon: <TrendingUp size={26} />, title: "Career Growth", desc: "Fast-track meritocratic advancement" },
  { icon: <Brain size={26} />, title: "Quantitative Rigour", desc: "Advanced AI and math research" },
  { icon: <Shield size={26} />, title: "Institutional Infrastructure", desc: "World-class execution tech" },
  { icon: <Users size={26} />, title: "Elite Peers", desc: "Learn alongside domain experts" }
];

const CAREERS_HERO_STATS = [
  { 
    icon: <Briefcase size={26} />, 
    label: "Open Roles", 
    value: `${openPositionsData.length} Positions`,
    targetId: "open-positions"
  },
  { icon: <MapPin size={26} />, label: "Headquarters", value: "Gurugram, India" },
  { icon: <Rocket size={26} />, label: "Growth", value: "Institutional Scale" }
];

// Search relevance scoring function for job roles ranking
const calculateRelevanceScore = (pos, query) => {
  if (!query) return 0;
  const q = query.toLowerCase().trim();
  if (!q) return 0;

  const words = q.split(/\s+/).filter(Boolean);
  const title = (pos.title || '').toLowerCase().trim();
  const dept = (pos.department || '').toLowerCase().trim();
  const desc = ((pos.aboutRole || pos.shortDescription || '') + ' ' + (pos.whyJoin || '')).toLowerCase();
  const skills = (pos.preferredSkills || []).map(s => s.toLowerCase());

  let score = 0;

  // Regex to check exact standalone word match in title (e.g., \bAI\b, \bCEO\b, \bData\b)
  const isExactWordInTitle = words.every(w => {
    const escaped = w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`\\b${escaped}\\b`, 'i').test(title);
  });

  // TIER 1: Exact Job Title Match (Case-insensitive)
  if (title === q) {
    score += 100000;
  }
  // TIER 2: Title Starts With Complete Search Phrase
  else if (title.startsWith(q)) {
    score += 80000;
    // Prefer shorter titles among starting matches
    score += Math.max(0, 1000 - title.length * 5);
  }
  // TIER 3: Title Contains Complete Search Phrase
  else if (title.includes(q)) {
    score += 60000;
    // Standalone word phrase bonus
    if (isExactWordInTitle) score += 10000;
    // Bonus for earlier occurrence in title
    score += Math.max(0, 2000 - title.indexOf(q) * 20);
    // Prefer shorter title for precision
    score += Math.max(0, 1000 - title.length * 5);
  }
  // TIER 4: Title Contains ALL Search Keywords
  else if (words.length > 1 && words.every(w => title.includes(w))) {
    score += 40000;
    if (isExactWordInTitle) score += 5000;
    score += Math.max(0, 1000 - title.length * 5);
  }
  // TIER 5: Title Contains Partial Search Keywords
  else {
    const matchingTitleWords = words.filter(w => title.includes(w));
    if (matchingTitleWords.length > 0) {
      score += 20000 + (matchingTitleWords.length * 3000);
      if (isExactWordInTitle) score += 2000;
    }
  }

  // TIER 6: Department & Skills Match (Higher priority than general description)
  if (dept === q) {
    score += 15000;
  } else if (dept.startsWith(q)) {
    score += 12000;
  } else if (dept.includes(q)) {
    score += 8000;
  } else {
    const matchingDeptWords = words.filter(w => dept.includes(w));
    if (matchingDeptWords.length > 0) {
      score += 4000 + (matchingDeptWords.length * 500);
    }
  }

  const skillExactMatch = skills.some(s => s === q);
  const skillPhraseMatch = skills.some(s => s.includes(q));
  if (skillExactMatch) {
    score += 12000;
  } else if (skillPhraseMatch) {
    score += 7000;
  } else {
    const matchingSkillWords = skills.filter(s => words.some(w => s.includes(w)));
    if (matchingSkillWords.length > 0) {
      score += 3000 + (matchingSkillWords.length * 400);
    }
  }

  // TIER 7: Description Keyword Relevance (Lowest Priority)
  if (desc.includes(q)) {
    score += 1000;
  } else {
    const matchingDescWords = words.filter(w => desc.includes(w));
    if (matchingDescWords.length > 0) {
      score += 200 + (matchingDescWords.length * 50);
    }
  }

  return score;
};

// Premium 4K Seamless Looping Cinematic Animation for Careers Page (Hexagonal Department Network & Fast Candlestick Flow)
const CareersInstitutionLoopCanvas = React.memo(() => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId;
    let width = 0;
    let height = 0;
    let dpr = 1;

    const updateDimensions = () => {
      if (!canvas.parentElement) return;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.parentElement.clientWidth || 1200;
      height = canvas.parentElement.clientHeight || 750;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.scale(dpr, dpr);
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    const COLOR_EMERALD = '#00ffd1';
    const COLOR_AQUA = '#00e5ff';
    const COLOR_GOLD = '#ffb703';
    const COLOR_PURPLE = '#a855f7';
    const COLOR_WHITE = '#ffffff';

    const departments = [
      { name: 'Quantitative Research', sub: 'Math & Neural Models', color: COLOR_EMERALD, relX: 0.22, relY: 0.32 },
      { name: 'Global Market Intelligence', sub: 'Macro & Data Feeds', color: COLOR_AQUA, relX: 0.50, relY: 0.22 },
      { name: 'Enterprise Risk', sub: '3D VaR & Analytics', color: '#ff4d4d', relX: 0.78, relY: 0.32 },
      { name: 'AI & Automation', sub: 'Machine Intelligence', color: COLOR_EMERALD, relX: 0.28, relY: 0.72 },
      { name: 'Technology Infrastructure', sub: 'Sub-ms Execution', color: COLOR_GOLD, relX: 0.50, relY: 0.78 },
      { name: 'Human Capital', sub: 'Talent & Operations', color: COLOR_PURPLE, relX: 0.72, relY: 0.72 }
    ];

    // Fast Market Candlesticks dataset
    const NUM_CANDLES = 40;
    const candlesData = [];
    for (let i = 0; i < NUM_CANDLES; i++) {
      const isBullish = i % 4 !== 0;
      candlesData.push({
        id: i,
        baseOffset: i / NUM_CANDLES,
        bodyWidth: 7 + (i % 5) * 2.5,
        heightRatio: 24 + (i * 17) % 46,
        wickTop: 8 + (i * 7) % 15,
        wickBottom: 6 + (i * 11) % 15,
        isBullish,
        yVariance: Math.sin(i * 1.5) * 65 + (i % 2 === 0 ? -20 : 20),
        speedMult: 1.2 + (i % 3) * 0.25
      });
    }

    let time = 0;
    const LOOP_DURATION = 14; // 14-second seamless loop with distinct sequential phases

    const render = () => {
      time += 0.024;
      ctx.clearRect(0, 0, width, height);

      const progress = (time % LOOP_DURATION) / LOOP_DURATION;

      // Calculate phase opacity for sequential playback
      // Phase 1 (0.0 - 0.50): Department Hexagon Network plays FIRST
      // Phase 2 (0.45 - 1.00): Fast Market Candlesticks movement plays NEXT
      let networkAlpha = 1.0;
      let candlesAlpha = 0.0;

      if (progress < 0.45) {
        networkAlpha = 1.0;
        candlesAlpha = 0.0;
      } else if (progress >= 0.45 && progress < 0.55) {
        // Crossfade transition between Phase 1 and Phase 2
        const p = (progress - 0.45) / 0.10;
        networkAlpha = 1.0 - p * 0.65; // Softens network to 0.35 background context
        candlesAlpha = p; // Ramps up candlestick movement
      } else if (progress >= 0.55 && progress < 0.92) {
        networkAlpha = 0.35;
        candlesAlpha = 1.0;
      } else {
        // Smooth loop reset back to Phase 1
        const p = (progress - 0.92) / 0.08;
        networkAlpha = 0.35 + p * 0.65;
        candlesAlpha = 1.0 - p;
      }

      // 1. Dark Institutional Canvas Background
      ctx.fillStyle = '#01050a';
      ctx.fillRect(0, 0, width, height);

      // Cyber-Financial Gradient Overlay
      const bgGrad = ctx.createLinearGradient(0, 0, width, height);
      bgGrad.addColorStop(0, 'rgba(0, 255, 209, 0.05)');
      bgGrad.addColorStop(0.5, 'rgba(1, 8, 18, 0.95)');
      bgGrad.addColorStop(1, 'rgba(0, 229, 255, 0.05)');
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, width, height);

      // Perspective Tech Grid
      ctx.strokeStyle = 'rgba(0, 255, 209, 0.05)';
      ctx.lineWidth = 1;
      const colStep = width / 12;
      for (let c = 0; c <= 12; c++) {
        ctx.beginPath();
        ctx.moveTo(c * colStep, 0);
        ctx.lineTo(c * colStep, height);
        ctx.stroke();
      }
      const rowStep = height / 8;
      for (let r = 0; r <= 8; r++) {
        ctx.beginPath();
        ctx.moveTo(0, r * rowStep);
        ctx.lineTo(width, r * rowStep);
        ctx.stroke();
      }

      // 2. Left Anchor (Quanentry Logo Side) & Right Anchor (Careers Page Side)
      const leftLogoX = Math.max(70, width * 0.08);
      const leftLogoY = 70;
      const rightCareersX = Math.min(width - 70, width * 0.92);
      const rightCareersY = 70;
      const centerY = height * 0.50;

      // Left Logo Anchor Badge
      ctx.save();
      ctx.font = '800 11px "JetBrains Mono", monospace';
      ctx.fillStyle = COLOR_EMERALD;
      ctx.textAlign = 'left';
      ctx.shadowColor = COLOR_EMERALD;
      ctx.shadowBlur = 10;
      ctx.fillText('◄ QUANENTRY LOGO', leftLogoX, leftLogoY - 18);
      ctx.beginPath();
      ctx.arc(leftLogoX + 15, leftLogoY, 10, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0, 255, 209, 0.25)';
      ctx.fill();
      ctx.strokeStyle = COLOR_EMERALD;
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();

      // Right Careers Anchor Badge
      ctx.save();
      ctx.font = '800 11px "JetBrains Mono", monospace';
      ctx.fillStyle = COLOR_AQUA;
      ctx.textAlign = 'right';
      ctx.shadowColor = COLOR_AQUA;
      ctx.shadowBlur = 10;
      ctx.fillText('CAREERS PORTAL ►', rightCareersX, rightCareersY - 18);
      ctx.beginPath();
      ctx.arc(rightCareersX - 15, rightCareersY, 10, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0, 229, 255, 0.25)';
      ctx.fill();
      ctx.strokeStyle = COLOR_AQUA;
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();

      // 3. PHASE 1 (FIRST): Hexagonal Interconnecting Department Network
      ctx.save();
      ctx.globalAlpha = networkAlpha;

      const depCoords = departments.map((dep) => ({
        ...dep,
        x: width * dep.relX,
        y: height * dep.relY + Math.sin(time * 2.5 + dep.relX * 10) * 12
      }));

      // Inter-Department Connection Lines & Data Packet Pulses
      depCoords.forEach((d1, i) => {
        depCoords.forEach((d2, j) => {
          if (i < j) {
            const pulse = Math.sin(time * 4 + i + j) * 0.5 + 0.5;
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(d1.x, d1.y);
            ctx.lineTo(d2.x, d2.y);
            ctx.strokeStyle = pulse > 0.4 ? 'rgba(0, 255, 209, 0.28)' : 'rgba(0, 229, 255, 0.15)';
            ctx.lineWidth = pulse > 0.4 ? 1.8 : 0.9;
            ctx.stroke();

            // Fast Data Packets traveling along lines
            const pkt = (time * 0.95 + i * 0.3 + j * 0.4) % 1;
            const px = d1.x + (d2.x - d1.x) * pkt;
            const py = d1.y + (d2.y - d1.y) * pkt;
            ctx.beginPath();
            ctx.arc(px, py, 3.0, 0, Math.PI * 2);
            ctx.fillStyle = COLOR_WHITE;
            ctx.shadowColor = COLOR_EMERALD;
            ctx.shadowBlur = 10;
            ctx.fill();
            ctx.restore();
          }
        });
      });

      // Draw Department Hexagonal Glass Pods
      depCoords.forEach((d) => {
        ctx.save();
        const hexRadius = 32;

        // Draw Hexagon Shape
        ctx.beginPath();
        for (let side = 0; side < 6; side++) {
          const angle = (side * Math.PI) / 3 + Math.PI / 6;
          const hx = d.x + hexRadius * Math.cos(angle);
          const hy = d.y + hexRadius * Math.sin(angle);
          if (side === 0) ctx.moveTo(hx, hy);
          else ctx.lineTo(hx, hy);
        }
        ctx.closePath();
        ctx.fillStyle = 'rgba(2, 12, 24, 0.92)';
        ctx.fill();
        ctx.strokeStyle = d.color;
        ctx.lineWidth = 2.0;
        ctx.shadowColor = d.color;
        ctx.shadowBlur = 14;
        ctx.stroke();

        // Inner Core Glow
        ctx.beginPath();
        ctx.arc(d.x, d.y, 6.5, 0, Math.PI * 2);
        ctx.fillStyle = d.color;
        ctx.fill();

        // Labels
        ctx.font = '700 10.5px "JetBrains Mono", monospace';
        ctx.fillStyle = COLOR_WHITE;
        ctx.textAlign = 'center';
        ctx.fillText(d.name, d.x, d.y + hexRadius + 15);

        ctx.font = '600 8.8px "JetBrains Mono", monospace';
        ctx.fillStyle = d.color;
        ctx.fillText(d.sub, d.x, d.y + hexRadius + 26);
        ctx.restore();
      });
      ctx.restore();

      // 4. PHASE 2 (THEN): Fast Market Candlesticks Trajectory (Left Quanentry Logo -> Network -> Right Careers)
      if (candlesAlpha > 0.01) {
        ctx.save();
        ctx.globalAlpha = candlesAlpha;

        // Local progression within Phase 2
        const candlePhaseTime = time * 1.4;

        candlesData.forEach((c) => {
          let candleProgress = (candlePhaseTime * 0.18 * c.speedMult + c.baseOffset) % 1.0;

          let cx, cy, alpha;

          if (candleProgress < 0.15) {
            // Emergence from Left Quanentry Logo side
            const p = candleProgress / 0.15;
            cx = leftLogoX + p * (width * 0.20);
            cy = leftLogoY + p * (centerY - leftLogoY) + c.yVariance * p;
            alpha = p;
          } else if (candleProgress < 0.80) {
            // Main Market Chart Wave crossing department network
            const p = (candleProgress - 0.15) / 0.65;
            cx = leftLogoX + width * 0.20 + p * (width * 0.60);
            const wave = Math.sin(p * Math.PI * 4 + candlePhaseTime * 2.5 + c.id) * 55;
            cy = centerY + c.yVariance * 0.7 + wave;
            alpha = 1.0;
          } else {
            // Convergence back to Right Careers side
            const p = (candleProgress - 0.80) / 0.20;
            const startX = leftLogoX + width * 0.80;
            const startY = centerY + c.yVariance * 0.7 + Math.sin(Math.PI * 4 + candlePhaseTime * 2.5 + c.id) * 55;
            cx = startX + p * (rightCareersX - startX);
            cy = startY + p * (rightCareersY - startY);
            alpha = 1.0 - p * 0.85; // Fades smoothly into Careers side
          }

          // Draw Fast Market Candlestick
          ctx.save();
          ctx.globalAlpha = Math.max(0, Math.min(1, alpha * candlesAlpha));

          const isBull = c.isBullish;
          const candleColor = isBull ? COLOR_EMERALD : COLOR_AQUA;
          const bodyH = c.heightRatio;
          const halfH = bodyH / 2;
          const bw = c.bodyWidth;

          // Wicks
          ctx.strokeStyle = candleColor;
          ctx.lineWidth = 1.6;
          ctx.shadowColor = candleColor;
          ctx.shadowBlur = 12;

          ctx.beginPath();
          ctx.moveTo(cx, cy - halfH - c.wickTop);
          ctx.lineTo(cx, cy + halfH + c.wickBottom);
          ctx.stroke();

          // Body
          ctx.fillStyle = isBull ? 'rgba(0, 255, 209, 0.92)' : 'rgba(0, 229, 255, 0.92)';
          ctx.fillRect(cx - bw / 2, cy - halfH, bw, bodyH);

          ctx.strokeStyle = COLOR_WHITE;
          ctx.lineWidth = 0.8;
          ctx.strokeRect(cx - bw / 2, cy - halfH, bw, bodyH);

          // Price indicator tags on periodic candles
          if (c.id % 6 === 0) {
            ctx.font = '600 8.5px "JetBrains Mono", monospace';
            ctx.fillStyle = COLOR_WHITE;
            ctx.textAlign = 'center';
            ctx.fillText(`$${(2100 + c.id * 18.5).toFixed(1)}`, cx, cy - halfH - c.wickTop - 4);
          }

          ctx.restore();
        });

        // Continuous Trend Line connecting candlestick motion
        ctx.beginPath();
        ctx.strokeStyle = 'rgba(0, 255, 209, 0.40)';
        ctx.lineWidth = 2;
        ctx.shadowColor = COLOR_EMERALD;
        ctx.shadowBlur = 12;
        for (let x = leftLogoX + 30; x <= rightCareersX - 30; x += 8) {
          const p = (x - (leftLogoX + 30)) / (rightCareersX - leftLogoX - 60);
          const y = centerY + Math.sin(p * Math.PI * 4 + candlePhaseTime * 2.5) * 45;
          if (x === leftLogoX + 30) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.restore();
      }

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', updateDimensions);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div className="careers-hero-canvas-bg" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'hidden', zIndex: 0 }}>
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%', opacity: 0.55, filter: 'brightness(0.65) contrast(1.05)' }} />
      <div className="careers-hero-overlay-dark" style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 50% 50%, rgba(1, 5, 10, 0.60) 0%, rgba(1, 5, 10, 0.82) 60%, rgba(1, 5, 10, 0.96) 100%)', pointerEvents: 'none', zIndex: 1 }} />
    </div>
  );
});

// Hero Quant Visual Overlay Component (Subtle Institutional Architectural Floor & Silhouettes)
const HeroQuantOverlay = React.memo(() => {
  return (
    <div className="hero-quant-overlay" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 1 }}>
      {/* Soft Ambient Depth Glows */}
      <div style={{
        position: 'absolute',
        top: '15%',
        left: '20%',
        width: '500px',
        height: '500px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(0, 255, 209, 0.08) 0%, rgba(102, 126, 234, 0.04) 50%, transparent 70%)',
        filter: 'blur(80px)',
        opacity: 0.8
      }}></div>
      <div style={{
        position: 'absolute',
        bottom: '10%',
        right: '15%',
        width: '600px',
        height: '600px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(102, 126, 234, 0.08) 0%, rgba(0, 255, 209, 0.03) 50%, transparent 70%)',
        filter: 'blur(90px)',
        opacity: 0.8
      }}></div>

      {/* Architectural Glass Wall & Professional Silhouettes Vector Layer */}
      <svg className="hero-vector-layer" style={{ position: 'absolute', width: '100%', height: '100%', opacity: 0.45 }}>
        <defs>
          <linearGradient id="glassGridGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(0, 255, 209, 0.15)" />
            <stop offset="50%" stopColor="rgba(255, 255, 255, 0.05)" />
            <stop offset="100%" stopColor="rgba(0, 255, 209, 0)" />
          </linearGradient>
          <linearGradient id="silhouetteGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(15, 30, 45, 0.9)" />
            <stop offset="100%" stopColor="rgba(5, 12, 22, 0.95)" />
          </linearGradient>
          <filter id="heroSubtleGlow">
            <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Architectural Glass Wall Structural Grid */}
        <g opacity="0.4">
          <line x1="12%" y1="0" x2="12%" y2="100%" stroke="url(#glassGridGrad)" strokeWidth="1" />
          <line x1="38%" y1="0" x2="38%" y2="100%" stroke="url(#glassGridGrad)" strokeWidth="1" />
          <line x1="62%" y1="0" x2="62%" y2="100%" stroke="url(#glassGridGrad)" strokeWidth="1" />
          <line x1="88%" y1="0" x2="88%" y2="100%" stroke="url(#glassGridGrad)" strokeWidth="1" />
          
          <line x1="0" y1="25%" x2="100%" y2="25%" stroke="rgba(255, 255, 255, 0.04)" strokeDasharray="4 8" />
          <line x1="0" y1="75%" x2="100%" y2="75%" stroke="rgba(255, 255, 255, 0.04)" strokeDasharray="4 8" />
        </g>

        {/* Subtle Architectural Horizon & Floor Lighting Line */}
        <line x1="0" y1="68%" x2="100%" y2="68%" stroke="rgba(0, 255, 209, 0.25)" strokeWidth="1" filter="url(#heroSubtleGlow)" />

        {/* BACKGROUND SILHOUETTES OF PROFESSIONALS (Subtle, Atmospheric, Luxury) */}

        {/* Small Group 1 (Left Distance): Researchers discussing at a glass wall */}
        <g opacity="0.3" transform="translate(60, 20)">
          <circle cx="120" cy="380" r="10" fill="url(#silhouetteGrad)" stroke="rgba(0, 255, 209, 0.4)" strokeWidth="1" />
          <path d="M 100 430 C 100 402, 115 397, 120 397 C 125 397, 140 402, 140 430 Z" fill="url(#silhouetteGrad)" stroke="rgba(0, 255, 209, 0.2)" strokeWidth="1" />
          
          <circle cx="155" cy="385" r="9.5" fill="url(#silhouetteGrad)" stroke="rgba(255, 255, 255, 0.3)" strokeWidth="1" />
          <path d="M 137 430 C 137 406, 150 401, 155 401 C 160 401, 173 406, 173 430 Z" fill="url(#silhouetteGrad)" stroke="rgba(255, 255, 255, 0.2)" strokeWidth="1" />
          
          {/* Glass discussion panel silhouette line */}
          <line x1="100" y1="350" x2="180" y2="350" stroke="rgba(0, 255, 209, 0.3)" strokeDasharray="2 4" />
        </g>

        {/* Small Group 2 (Right Distance): Team members in a collaborative research session */}
        <g opacity="0.3" transform="translate(-40, 10)">
          <circle cx="820" cy="380" r="10" fill="url(#silhouetteGrad)" stroke="rgba(102, 126, 234, 0.4)" strokeWidth="1" />
          <path d="M 800 430 C 800 402, 815 397, 820 397 C 825 397, 840 402, 840 430 Z" fill="url(#silhouetteGrad)" stroke="rgba(102, 126, 234, 0.2)" strokeWidth="1" />

          <circle cx="860" cy="383" r="9" fill="url(#silhouetteGrad)" stroke="rgba(0, 255, 209, 0.3)" strokeWidth="1" />
          <path d="M 843 430 C 843 404, 855 400, 860 400 C 865 400, 877 404, 877 430 Z" fill="url(#silhouetteGrad)" stroke="rgba(0, 255, 209, 0.2)" strokeWidth="1" />
        </g>
      </svg>
    </div>
  );
});

// Institutional Culture & Values Graphic Component: "THE QUANENTRY WAY"
const WhyVisualGraphic = React.memo(() => {
  const [rotate, setRotate] = useState({ x: 0, y: 0 });
  const [activeNode, setActiveNode] = useState(null);
  const rafRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.clientX;
    const clientY = e.clientY;

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      const x = (clientX - rect.left - rect.width / 2) / 35;
      const y = (clientY - rect.top - rect.height / 2) / 35;
      setRotate({ x: -y, y: x });
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    setRotate({ x: 0, y: 0 });
    setActiveNode(null);
  }, []);

  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return (
    <div 
      className="quant-workspace-visual"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: '600px',
        margin: '0 auto',
        perspective: '1000px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {/* Background Volumetric Glow Aura */}
      <div className="quant-visual-auras">
        <motion.div 
          animate={{
            scale: [0.95, 1.08, 0.95],
            opacity: [0.7, 0.95, 0.7]
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            position: 'absolute',
            width: '500px',
            height: '500px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0, 255, 209, 0.28) 0%, rgba(102, 126, 234, 0.16) 50%, transparent 75%)',
            filter: 'blur(60px)',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 0
          }}
        />
      </div>

      <motion.div
        className="quant-matrix-card"
        animate={{
          rotateX: rotate.x,
          rotateY: rotate.y,
        }}
        transition={{ type: "spring", stiffness: 140, damping: 18 }}
        style={{
          position: 'relative',
          width: '100%',
          minHeight: '540px',
          borderRadius: '24px',
          border: '1px solid rgba(0, 255, 209, 0.38)',
          boxShadow: '0 25px 65px rgba(0, 0, 0, 0.85), inset 0 0 45px rgba(0, 255, 209, 0.08)',
          backdropFilter: 'blur(22px)',
          overflow: 'hidden',
          zIndex: 1,
          background: 'linear-gradient(155deg, rgba(8, 20, 32, 0.98) 0%, rgba(2, 6, 14, 0.99) 100%)',
          padding: '30px 32px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: '24px'
        }}
      >
        {/* Terminal Header */}
        <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(0, 255, 209, 0.2)', paddingBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <motion.span 
              animate={{ scale: [1, 1.35, 1], opacity: [0.85, 1, 0.85] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
              style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#00ffd1', boxShadow: '0 0 14px #00ffd1' }}
            />
            <span style={{ fontSize: '12px', fontFamily: 'monospace', color: '#ffffff', letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: '700' }}>
              THE QUANENTRY WAY
            </span>
          </div>
          <span style={{ fontSize: '11px', fontFamily: 'monospace', color: '#00ffd1', background: 'rgba(0, 255, 209, 0.12)', padding: '5px 14px', borderRadius: '50px', border: '1px solid rgba(0, 255, 209, 0.35)', letterSpacing: '0.8px', fontWeight: '600' }}>
            OUR CULTURE
          </span>
        </div>

        {/* Interactive Culture Diagram Graphic */}
        <div style={{ position: 'relative', width: '100%', height: '310px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="100%" height="100%" viewBox="0 0 480 320" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <radialGradient id="centerCoreGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#00ffd1" stopOpacity="0.45" />
                <stop offset="60%" stopColor="#00ffd1" stopOpacity="0.08" />
                <stop offset="100%" stopColor="transparent" stopOpacity="0" />
              </radialGradient>

              <filter id="teamCyanGlow" x="-30%" y="-30%" width="160%" height="160%">
                <feGaussianBlur stdDeviation="6" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Glowing Central Pulse Area */}
            <circle cx="240" cy="160" r="95" fill="url(#centerCoreGlow)" />
            <motion.circle 
              cx="240" 
              cy="160" 
              r="62" 
              fill="none" 
              stroke="rgba(0, 255, 209, 0.4)" 
              strokeWidth="1.5"
              strokeDasharray="6 6"
              animate={{ rotate: 360 }}
              transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
              style={{ transformOrigin: '240px 160px' }}
            />
            <motion.circle 
              cx="240" 
              cy="160" 
              r="84" 
              fill="none" 
              stroke="rgba(102, 126, 234, 0.3)" 
              strokeWidth="1.2"
              strokeDasharray="8 8"
              animate={{ rotate: -360 }}
              transition={{ duration: 32, repeat: Infinity, ease: "linear" }}
              style={{ transformOrigin: '240px 160px' }}
            />

            {/* Connecting Synapse Beams from 4 Value Nodes to Central Core */}
            {[
              { x1: 95, y1: 65, x2: 240, y2: 160, id: 'excellence' },
              { x1: 385, y1: 65, x2: 240, y2: 160, id: 'integrity' },
              { x1: 95, y1: 255, x2: 240, y2: 160, id: 'ownership' },
              { x1: 385, y1: 255, x2: 240, y2: 160, id: 'collaboration' }
            ].map((beam, idx) => {
              const isHovered = activeNode === beam.id;
              return (
                <g key={idx}>
                  <line 
                    x1={beam.x1} 
                    y1={beam.y1} 
                    x2={beam.x2} 
                    y2={beam.y2} 
                    stroke={isHovered ? "#00ffd1" : "rgba(0, 255, 209, 0.38)"} 
                    strokeWidth={isHovered ? "2.8" : "1.6"}
                    strokeDasharray={isHovered ? "none" : "5 5"}
                  />
                  {/* Pulse Particle Traveling Along Vector */}
                  <motion.circle
                    r="4"
                    fill="#00ffd1"
                    filter="url(#teamCyanGlow)"
                    animate={{
                      cx: [beam.x1, beam.x2],
                      cy: [beam.y1, beam.y2]
                    }}
                    transition={{
                      duration: 2.2 + idx * 0.4,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
                </g>
              );
            })}

            {/* CENTRAL QUANENTRY CAPITAL CORE */}
            <g transform="translate(240, 160)">
              <motion.circle
                r="44"
                fill="rgba(4, 16, 30, 0.96)"
                stroke="#00ffd1"
                strokeWidth="2.5"
                filter="url(#teamCyanGlow)"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              />
              <circle r="35" fill="rgba(0, 255, 209, 0.14)" />
              <text x="0" y="-4" textAnchor="middle" fill="#00ffd1" fontSize="12.5" fontFamily="monospace" fontWeight="800" letterSpacing="1px">QUANENTRY</text>
              <text x="0" y="12" textAnchor="middle" fill="#ffffff" fontSize="10" fontFamily="monospace" fontWeight="700" letterSpacing="1.8px">CAPITAL</text>
            </g>

            {/* 4 INSTITUTIONAL VALUE NODES (ENLARGED +20%) */}
            {[
              { 
                id: 'excellence', 
                title: 'EXCELLENCE', 
                lines: ['Pursue exceptional standards.'], 
                cx: 95, 
                cy: 65 
              },
              { 
                id: 'integrity', 
                title: 'INTEGRITY', 
                lines: ['Always do what is right.'], 
                cx: 385, 
                cy: 65 
              },
              { 
                id: 'ownership', 
                title: 'OWNERSHIP', 
                lines: ['Take responsibility.', 'Create lasting impact.'], 
                cx: 95, 
                cy: 255 
              },
              { 
                id: 'collaboration', 
                title: 'COLLABORATION', 
                lines: ['Great achievements', 'are built together.'], 
                cx: 385, 
                cy: 255 
              }
            ].map((node) => {
              const isActive = activeNode === node.id;
              const hasTwoLines = node.lines.length > 1;
              return (
                <g 
                  key={node.id} 
                  transform={`translate(${node.cx}, ${node.cy})`}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setActiveNode(node.id)}
                  onMouseLeave={() => setActiveNode(null)}
                >
                  {/* Outer Pulsing Capsule Card */}
                  <motion.rect
                    x="-83"
                    y="-31"
                    width="166"
                    height="62"
                    rx="18"
                    fill={isActive ? "rgba(0, 255, 209, 0.22)" : "rgba(6, 20, 34, 0.96)"}
                    stroke={isActive ? "#00ffd1" : "rgba(0, 255, 209, 0.6)"}
                    strokeWidth={isActive ? "2.2" : "1.6"}
                    filter={isActive ? "url(#teamCyanGlow)" : "none"}
                    animate={{ scale: isActive ? 1.06 : 1 }}
                    transition={{ duration: 0.2 }}
                  />
                  <text 
                    x="0" 
                    y={hasTwoLines ? "-12" : "-7"} 
                    textAnchor="middle" 
                    fill="#ffffff" 
                    fontSize="11.5" 
                    fontFamily="sans-serif" 
                    fontWeight="800" 
                    letterSpacing="0.6px"
                  >
                    {node.title}
                  </text>
                  {hasTwoLines ? (
                    <>
                      <text x="0" y="5" textAnchor="middle" fill="#00ffd1" fontSize="8.5" fontFamily="monospace" fontWeight="600" letterSpacing="0.2px">
                        {node.lines[0]}
                      </text>
                      <text x="0" y="17" textAnchor="middle" fill="#00ffd1" fontSize="8.5" fontFamily="monospace" fontWeight="600" letterSpacing="0.2px">
                        {node.lines[1]}
                      </text>
                    </>
                  ) : (
                    <text x="0" y="11" textAnchor="middle" fill="#00ffd1" fontSize="9" fontFamily="monospace" fontWeight="600" letterSpacing="0.2px">
                      {node.lines[0]}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>

        {/* INSTITUTIONAL CULTURE QUOTE CARD */}
        <div style={{
          background: 'rgba(0, 255, 209, 0.06)',
          border: '1px solid rgba(0, 255, 209, 0.28)',
          borderRadius: '16px',
          padding: '20px 24px',
          position: 'relative'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <span style={{ fontSize: '36px', color: '#00ffd1', lineHeight: '0.8', fontFamily: 'Georgia, serif', fontWeight: 'bold' }}>“</span>
            <div style={{ width: '100%' }}>
              <p style={{
                fontSize: '14.5px',
                lineHeight: '1.7',
                color: '#ffffff',
                fontWeight: '400',
                fontStyle: 'italic',
                margin: 0,
                fontFamily: 'sans-serif',
                letterSpacing: '0.2px'
              }}>
                Extraordinary organizations are not defined by departments. They are defined by the standards every individual chooses to uphold.
              </p>
              <div style={{
                marginTop: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderTop: '1px solid rgba(0, 255, 209, 0.2)',
                paddingTop: '12px'
              }}>
                <span style={{ fontSize: '12px', fontFamily: 'monospace', color: '#00ffd1', fontWeight: '700', letterSpacing: '1px' }}>
                  — THE QUANENTRY WAY
                </span>
                <span style={{ fontSize: '11px', fontFamily: 'monospace', color: 'rgba(255, 255, 255, 0.85)', letterSpacing: '0.8px', fontWeight: '600' }}>
                  ONE STANDARD • MANY EXPERTISES
                </span>
              </div>
            </div>
          </div>
        </div>

      </motion.div>
    </div>
  );
});

/* Custom Horizontal Carousel for Department Filter */
const DepartmentCarousel = React.memo(({ departments, selectedDepartment, onSelectDepartment }) => {
  const containerRef = useRef(null);
  const activeTabRef = useRef(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeftState, setScrollLeftState] = useState(0);
  const [hasDragged, setHasDragged] = useState(false);

  const checkScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 3);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 3);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    checkScroll();

    const handleResize = () => checkScroll();
    window.addEventListener('resize', handleResize);

    let resizeObserver;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => checkScroll());
      resizeObserver.observe(el);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeObserver && el) resizeObserver.unobserve(el);
    };
  }, [checkScroll, departments]);

  // Center active department chip automatically
  useEffect(() => {
    if (activeTabRef.current && containerRef.current) {
      const container = containerRef.current;
      const chip = activeTabRef.current;

      const chipOffsetLeft = chip.offsetLeft;
      const chipWidth = chip.offsetWidth;
      const containerWidth = container.clientWidth;

      const targetScrollLeft = chipOffsetLeft - containerWidth / 2 + chipWidth / 2;

      container.scrollTo({
        left: Math.max(0, targetScrollLeft),
        behavior: 'smooth'
      });

      const timer = setTimeout(() => {
        checkScroll();
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [selectedDepartment, checkScroll]);

  const handleScroll = (direction) => {
    const el = containerRef.current;
    if (!el) return;
    const scrollAmount = Math.max(220, el.clientWidth * 0.6);
    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
    setTimeout(() => checkScroll(), 350);
  };

  const handleWheel = (e) => {
    const el = containerRef.current;
    if (!el) return;
    if (e.deltaY !== 0 && e.deltaX === 0) {
      el.scrollLeft += e.deltaY;
      checkScroll();
    }
  };

  const handleMouseDown = (e) => {
    const el = containerRef.current;
    if (!el) return;
    setIsDragging(true);
    setHasDragged(false);
    setStartX(e.pageX - el.offsetLeft);
    setScrollLeftState(el.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const el = containerRef.current;
    if (!el) return;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    const walk = (x - startX) * 1.5;
    if (Math.abs(walk) > 5) {
      setHasDragged(true);
    }
    el.scrollLeft = scrollLeftState - walk;
    checkScroll();
  };

  const handleTabClick = (dept) => {
    if (hasDragged) return;
    onSelectDepartment(dept);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const nextIdx = (index + 1) % departments.length;
      onSelectDepartment(departments[nextIdx]);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prevIdx = (index - 1 + departments.length) % departments.length;
      onSelectDepartment(departments[prevIdx]);
    }
  };

  return (
    <div className="department-tabs-wrapper">
      <div className="dept-carousel-outer">
        {/* Left Navigation Arrow */}
        <button
          type="button"
          className={`dept-carousel-btn dept-carousel-btn-left ${!canScrollLeft ? 'disabled' : ''}`}
          onClick={() => handleScroll('left')}
          disabled={!canScrollLeft}
          aria-label="Scroll left departments"
        >
          <ChevronLeft size={18} />
        </button>

        {/* Left Fade Overlay */}
        <div className={`dept-carousel-fade dept-carousel-fade-left ${canScrollLeft ? 'active' : ''}`} />

        {/* Scrollable Track */}
        <div
          ref={containerRef}
          className={`dept-carousel-track ${isDragging ? 'dragging' : ''}`}
          onScroll={checkScroll}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          data-testid="department-tabs"
        >
          {departments.map((dept, idx) => {
            const isActive = selectedDepartment === dept;
            return (
              <button
                key={idx}
                ref={isActive ? activeTabRef : null}
                type="button"
                className={`dept-tab ${isActive ? 'active' : ''}`}
                onClick={() => handleTabClick(dept)}
                onKeyDown={(e) => handleKeyDown(e, idx)}
                data-testid={`dept-tab-${dept.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {dept === "All" ? "All Departments" : dept}
              </button>
            );
          })}
        </div>

        {/* Right Fade Overlay */}
        <div className={`dept-carousel-fade dept-carousel-fade-right ${canScrollRight ? 'active' : ''}`} />

        {/* Right Navigation Arrow */}
        <button
          type="button"
          className={`dept-carousel-btn dept-carousel-btn-right ${!canScrollRight ? 'disabled' : ''}`}
          onClick={() => handleScroll('right')}
          disabled={!canScrollRight}
          aria-label="Scroll right departments"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
});

/* Compact Job Card Component - Memoized to eliminate scroll re-rendering lag */
const JobCardCompact = React.memo(({ pos, onViewDetails }) => {
  const DeptIcon = departmentIcons[pos.department] || Layers;

  return (
    <motion.div 
      className="job-card-compact"
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      data-testid={`job-card-${pos.id}`}
    >
      <div className="job-card-top">
        <div className="job-card-dept-badge">
          <DeptIcon size={14} />
          <span>{pos.department}</span>
        </div>
        <h3 className="job-card-title">{pos.title}</h3>
        <p className="job-card-summary">{pos.shortDescription}</p>
      </div>

      <div>
        <div className="job-card-meta-grid">
          <div className="meta-item" title={pos.location}>
            <MapPin size={14} />
            <span>{pos.location}</span>
          </div>
          <div className="meta-item" title={pos.experienceRequired}>
            <Clock size={14} />
            <span>{pos.experienceRequired}</span>
          </div>
          <div className="meta-item" title={pos.employmentType}>
            <Briefcase size={14} />
            <span>{pos.employmentType}</span>
          </div>
          <div className="meta-item status-highlight" title={pos.status}>
            <CheckCircle2 size={14} />
            <span>{pos.status}</span>
          </div>
        </div>

        <div className="job-card-footer">
          <button 
            type="button"
            className="btn-view-details"
            onClick={() => onViewDetails(pos)}
            data-testid={`view-details-${pos.id}`}
          >
            View Details
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </motion.div>
  );
});

/* Job Details Slide-Over Modal Component - Memoized for isolated render tree */
const JobDetailsSlideOver = React.memo(({ selectedJobDetails, onClose, onApply }) => {
  if (!selectedJobDetails) return null;

  const DeptIcon = departmentIcons[selectedJobDetails.department] || Layers;

  return (
    <>
      {/* Blurred Backdrop */}
      <motion.div 
        className="panel-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="panel-backdrop"
      />

      {/* Slide-over Panel (Desktop) / Modal (Mobile) */}
      <motion.div 
        className="slide-over-panel"
        initial={{ x: "100%", opacity: 0.8 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: "100%", opacity: 0 }}
        transition={{ type: "spring", stiffness: 320, damping: 32 }}
        data-testid="job-details-panel"
      >
        {/* Header */}
        <div className="panel-header">
          <button 
            type="button"
            className="panel-close-btn"
            onClick={onClose}
            aria-label="Close details panel"
            data-testid="close-panel-btn"
          >
            <X size={18} />
          </button>

          <div className="job-card-dept-badge">
            <DeptIcon size={14} />
            <span>{selectedJobDetails.department}</span>
          </div>

          <h2 className="panel-title">{selectedJobDetails.title}</h2>
        </div>

        {/* Scrollable Body */}
        <div className="panel-content-body">
          {/* Key Meta Information */}
          <div className="panel-meta-box">
            <div className="panel-meta-cell">
              <span className="panel-meta-label"><MapPin size={12} /> Location</span>
              <span className="panel-meta-value">{selectedJobDetails.location}</span>
            </div>
            <div className="panel-meta-cell">
              <span className="panel-meta-label"><Clock size={12} /> Experience</span>
              <span className="panel-meta-value">{selectedJobDetails.experienceRequired}</span>
            </div>
            <div className="panel-meta-cell">
              <span className="panel-meta-label"><Briefcase size={12} /> Employment Type</span>
              <span className="panel-meta-value">{selectedJobDetails.employmentType}</span>
            </div>
            <div className="panel-meta-cell">
              <span className="panel-meta-label"><UserCheck size={12} /> Reporting To</span>
              <span className="panel-meta-value">
                {selectedJobDetails.reportingManager || (
                  selectedJobDetails.department.includes("Office of the CEO")
                    ? "Chief Executive Officer"
                    : `Head – ${selectedJobDetails.department.replace(" Division", "")}`
                )}
              </span>
            </div>
          </div>

          {/* Position Overview */}
          <div className="panel-section-block">
            <h3 className="panel-section-title"><UserCheck size={18} /> About the Role</h3>
            <p className="panel-section-text">{selectedJobDetails.aboutRole || selectedJobDetails.shortDescription}</p>
          </div>

          {/* Key Responsibilities */}
          {selectedJobDetails.keyResponsibilities && selectedJobDetails.keyResponsibilities.length > 0 && (
            <div className="panel-section-block">
              <h3 className="panel-section-title"><CheckCircle2 size={18} /> Key Responsibilities</h3>
              <ul className="panel-bullet-list">
                {selectedJobDetails.keyResponsibilities.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Required Qualifications & Education */}
          <div className="panel-section-block">
            <h3 className="panel-section-title"><GraduationCap size={18} /> Required Qualifications & Education</h3>
            {selectedJobDetails.education && (
              <p className="panel-section-text" style={{ marginBottom: '10px' }}>
                <strong>Education:</strong> {selectedJobDetails.education}
              </p>
            )}
            {selectedJobDetails.requiredQualifications && selectedJobDetails.requiredQualifications.length > 0 && (
              <ul className="panel-bullet-list">
                {selectedJobDetails.requiredQualifications.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            )}
          </div>

          {/* Skills */}
          {selectedJobDetails.preferredSkills && selectedJobDetails.preferredSkills.length > 0 && (
            <div className="panel-section-block">
              <h3 className="panel-section-title"><Code size={18} /> Preferred Skills & Competencies</h3>
              <div className="panel-skills-container">
                {selectedJobDetails.preferredSkills.map((skill, i) => (
                  <span key={i} className="panel-skill-tag">{skill}</span>
                ))}
              </div>
            </div>
          )}

          {/* Benefits & Culture */}
          {selectedJobDetails.whyJoin && (
            <div className="panel-section-block panel-why-join-card">
              <h3 className="panel-section-title"><Sparkles size={18} /> Benefits & Institutional Culture</h3>
              <p className="panel-section-text">{selectedJobDetails.whyJoin}</p>
            </div>
          )}
        </div>

        {/* Sticky Footer */}
        <div className="panel-footer">
          <button 
            type="button"
            className="panel-apply-btn"
            onClick={() => onApply(selectedJobDetails)}
            data-testid={`panel-apply-btn-${selectedJobDetails.id}`}
          >
            Apply Now For Position
            <ArrowRight size={18} />
          </button>
        </div>
      </motion.div>
    </>
  );
});

// Static form option dropdowns (Extracted to prevent re-creation on renders)
const GURUGRAM_WORK_OPTIONS = ["Yes", "No"];
const EMPLOYMENT_STATUS_OPTIONS = [
  "Working Full-Time",
  "Serving Notice Period",
  "Immediate Joiner",
  "Student / Fresher",
  "Self-Employed / Freelance",
  "Not Currently Employed"
];
const EXPERIENCE_OPTIONS = [
  "Fresher",
  "Less than 1 Year",
  "1–3 Years",
  "3–5 Years",
  "5–8 Years",
  "8–12 Years",
  "12+ Years"
];
const QUALIFICATION_OPTIONS = [
  "High School",
  "Diploma",
  "Bachelor's Degree",
  "Master's Degree",
  "MBA",
  "CA",
  "CS",
  "CMA",
  "PhD",
  "Other"
];
const NOTICE_PERIOD_OPTIONS = [
  "Immediate",
  "15 Days",
  "30 Days",
  "60 Days",
  "90 Days",
  "Currently Serving Notice Period"
];

const CareersUltra = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const containerRef = useRef(null);
  const applyFormRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0.8]);

  const [rolesList, setRolesList] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [selectedJobDetails, setSelectedJobDetails] = useState(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSelectedJobDetails(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState('');
  
  const [submissionResult, setSubmissionResult] = useState(null);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    city: '',
    work_location_gurugram: '',
    department: '',
    job_title: '',
    experience_level: '',
    highest_qualification: '',
    employment_status: '',
    current_ctc: '',
    expected_ctc: '',
    compensation_justification: '',
    notice_period: '',
    cover_letter: '',
    declaration_accepted: false,
    website: ''
  });

  // Calculate CTC increase percentage dynamically
  const ctcIncreasePercent = calculateCtcIncreasePercentage(formData.current_ctc, formData.expected_ctc);
  
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeFileName, setResumeFileName] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const gurugramWorkOptions = GURUGRAM_WORK_OPTIONS;
  const employmentStatusOptions = EMPLOYMENT_STATUS_OPTIONS;
  const experienceOptions = EXPERIENCE_OPTIONS;
  const qualificationOptions = QUALIFICATION_OPTIONS;
  const noticePeriodOptions = NOTICE_PERIOD_OPTIONS;

  useEffect(() => {
    fetchRolesFromApi();
  }, []);

  const fetchRolesFromApi = async () => {
    try {
      const response = await axios.get(`${API}/careers/roles`);
      if (response.data && response.data.roles) {
        setRolesList(response.data.roles);
      }
    } catch (err) {
      console.log('Using static open positions list');
    }
  };

  // Unique departments for filter and form selection
  const departments = useMemo(() => ["All", ...Array.from(new Set(openPositionsData.map(p => p.department)))], []);
  const availableDepartments = useMemo(() => Array.from(new Set(openPositionsData.map(p => p.department))), []);

  // Filtered job titles based on selected department in application form
  const availableJobTitles = useMemo(() => {
    return formData.department
      ? openPositionsData.filter(p => p.department === formData.department).map(p => p.title)
      : [];
  }, [formData.department]);

  // Filtered positions for job board listing - Uses deferredSearchQuery & relevance scoring algorithm
  const filteredPositions = useMemo(() => {
    const q = deferredSearchQuery.toLowerCase().trim();
    const words = q.split(/\s+/).filter(Boolean);

    const matches = openPositionsData.filter(pos => {
      const matchesDept = selectedDepartment === "All" || pos.department === selectedDepartment;
      const matchesSearch = !q || 
        pos.title.toLowerCase().includes(q) || 
        pos.department.toLowerCase().includes(q) ||
        pos.shortDescription.toLowerCase().includes(q) ||
        (pos.aboutRole && pos.aboutRole.toLowerCase().includes(q)) ||
        pos.preferredSkills.some(s => s.toLowerCase().includes(q)) ||
        (words.length > 1 && words.every(w => 
          pos.title.toLowerCase().includes(w) ||
          pos.department.toLowerCase().includes(w) ||
          pos.shortDescription.toLowerCase().includes(w) ||
          pos.preferredSkills.some(s => s.toLowerCase().includes(w))
        ));
      return matchesDept && matchesSearch;
    });

    if (!q) {
      return matches;
    }

    // Sort matching positions by calculated relevance score descending
    return matches.slice().sort((a, b) => {
      const scoreA = calculateRelevanceScore(a, q);
      const scoreB = calculateRelevanceScore(b, q);
      return scoreB - scoreA;
    });
  }, [selectedDepartment, deferredSearchQuery]);

  const handleSelectJobDetails = useCallback((pos) => {
    setSelectedJobDetails(pos);
  }, []);

  const handleCloseJobDetails = useCallback(() => {
    setSelectedJobDetails(null);
  }, []);

  // Handle direct "Apply Now" click on a job card - Memoized for rendering efficiency
  const handleApplyForRole = useCallback((pos) => {
    const dept = typeof pos === 'string' ? '' : pos.department;
    const title = typeof pos === 'string' ? pos : pos.title;
    setFormData(prev => ({
      ...prev,
      ...(dept ? { department: dept } : {}),
      job_title: title
    }));
    setNotification(`Selected Position: ${title}${dept ? ` (${dept})` : ''}`);
    if (applyFormRef.current) {
      applyFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleApplyFromSlideOver = useCallback((pos) => {
    handleApplyForRole(pos);
    setSelectedJobDetails(null);
  }, [handleApplyForRole]);

  const handleSelectDepartment = useCallback((dept) => {
    setSelectedDepartment(dept);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
  }, []);

  const handleResetFilters = useCallback(() => {
    setSelectedDepartment("All");
    setSearchQuery("");
  }, []);

  const handleDepartmentChange = (e) => {
    const newDept = e.target.value;
    setFormData(prev => ({
      ...prev,
      department: newDept,
      job_title: '' // Reset job title when department changes
    }));
    setError('');
    setFieldErrors(prev => ({ ...prev, department: '', job_title: '' }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const isPdfExt = file.name.toLowerCase().endsWith('.pdf');
      const isPdfMime = file.type === 'application/pdf';
      if (!isPdfMime && !isPdfExt) {
        setError('Resume must be a PDF file only.');
        setFieldErrors(prev => ({ ...prev, resume: 'Resume must be a PDF file only.' }));
        setResumeFile(null);
        setResumeFileName('');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Resume file size must not exceed 5 MB.');
        setFieldErrors(prev => ({ ...prev, resume: 'Resume file size must not exceed 5 MB.' }));
        setResumeFile(null);
        setResumeFileName('');
        return;
      }
      setResumeFile(file);
      setResumeFileName(file.name);
      setError('');
      setFieldErrors(prev => ({ ...prev, resume: '' }));
    }
  };

  const validateForm = () => {
    const newFieldErrors = {};
    let firstErrorId = null;

    if (!formData.full_name.trim()) {
      newFieldErrors.full_name = 'Full Name is required.';
      if (!firstErrorId) firstErrorId = 'full_name';
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newFieldErrors.email = 'Valid Email Address is required.';
      if (!firstErrorId) firstErrorId = 'email';
    }
    if (!formData.phone.trim() || formData.phone.replace(/[^0-9]/g, '').length < 10) {
      newFieldErrors.phone = 'Valid Mobile Number (at least 10 digits) is required.';
      if (!firstErrorId) firstErrorId = 'phone';
    }
    if (!formData.city.trim()) {
      newFieldErrors.city = 'Current City is required.';
      if (!firstErrorId) firstErrorId = 'city';
    }
    if (!formData.work_location_gurugram) {
      newFieldErrors.work_location_gurugram = 'Willingness to work from Gurugram Office selection is required.';
      if (!firstErrorId) firstErrorId = 'work_location_gurugram';
    }
    if (!formData.department) {
      newFieldErrors.department = 'Department selection is required.';
      if (!firstErrorId) firstErrorId = 'department';
    }
    if (!formData.job_title) {
      newFieldErrors.job_title = 'Job Title selection is required.';
      if (!firstErrorId) firstErrorId = 'job_title';
    }
    if (!formData.experience_level) {
      newFieldErrors.experience_level = 'Total Professional Experience selection is required.';
      if (!firstErrorId) firstErrorId = 'experience_level';
    }
    if (!formData.highest_qualification) {
      newFieldErrors.highest_qualification = 'Highest Qualification selection is required.';
      if (!firstErrorId) firstErrorId = 'highest_qualification';
    }
    if (!formData.employment_status) {
      newFieldErrors.employment_status = 'Current Employment Status selection is required.';
      if (!firstErrorId) firstErrorId = 'employment_status';
    }
    if (!formData.current_ctc || !String(formData.current_ctc).trim()) {
      newFieldErrors.current_ctc = 'Current CTC is required.';
      if (!firstErrorId) firstErrorId = 'current_ctc';
    }
    if (!formData.expected_ctc || !String(formData.expected_ctc).trim()) {
      newFieldErrors.expected_ctc = 'Expected CTC is required.';
      if (!firstErrorId) firstErrorId = 'expected_ctc';
    }
    if (!formData.notice_period) {
      newFieldErrors.notice_period = 'Notice Period selection is required.';
      if (!firstErrorId) firstErrorId = 'notice_period';
    }
    if (!resumeFile) {
      newFieldErrors.resume = 'PDF Resume upload is required.';
      if (!firstErrorId) firstErrorId = 'resume';
    }
    const coverLen = formData.cover_letter.trim().length;
    if (coverLen < 100) {
      newFieldErrors.cover_letter = `Cover Letter must contain at least 100 characters (currently ${coverLen}).`;
      if (!firstErrorId) firstErrorId = 'cover_letter';
    } else if (coverLen > 4000) {
      newFieldErrors.cover_letter = `Cover Letter must not exceed 4000 characters (currently ${coverLen}).`;
      if (!firstErrorId) firstErrorId = 'cover_letter';
    }
    if (!formData.declaration_accepted) {
      newFieldErrors.declaration_accepted = 'You must accept the declaration to submit your application.';
      if (!firstErrorId) firstErrorId = 'declaration_accepted_checkbox';
    }

    setFieldErrors(newFieldErrors);

    if (firstErrorId) {
      const topErrorKey = firstErrorId === 'declaration_accepted_checkbox' ? 'declaration_accepted' : (firstErrorId === 'resume' ? 'resume' : firstErrorId);
      setError(newFieldErrors[topErrorKey]);

      setTimeout(() => {
        const el = document.getElementById(firstErrorId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (typeof el.focus === 'function') el.focus();
        }
      }, 50);

      return false;
    }

    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      let recaptchaToken = '';
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('careers_apply');
        } catch (e) {
          // Token optional if provider unconfigured
        }
      }

      const submitData = new FormData();
      submitData.append('full_name', formData.full_name.trim());
      submitData.append('email', formData.email.trim());
      submitData.append('phone', formData.phone.trim());
      submitData.append('city', formData.city.trim());
      submitData.append('work_location_gurugram', formData.work_location_gurugram);
      submitData.append('department', formData.department);
      submitData.append('job_title', formData.job_title);
      submitData.append('role_applied', formData.job_title);
      submitData.append('experience_level', formData.experience_level);
      submitData.append('highest_qualification', formData.highest_qualification);
      submitData.append('employment_status', formData.employment_status);
      submitData.append('current_ctc', formData.current_ctc || '');
      submitData.append('expected_ctc', formData.expected_ctc || '');
      submitData.append('ctc_increase_percentage', ctcIncreasePercent > 0 ? `${ctcIncreasePercent.toFixed(1)}%` : '0%');
      submitData.append('compensation_justification', formData.compensation_justification || '');
      submitData.append('notice_period', formData.notice_period);
      submitData.append('cover_letter', formData.cover_letter.trim());
      submitData.append('cover_note', formData.cover_letter.trim());
      submitData.append('consent_given', 'true');
      submitData.append('recaptcha_token', recaptchaToken);
      submitData.append('website', formData.website || '');
      submitData.append('resume', resumeFile);

      const res = await axios.post(`${API}/careers/apply`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data) {
        const app = res.data.application || {};
        setSubmissionResult({
          application_id: res.data.application_id || app.application_id || 'QC-SUCCESS',
          job_title: app.job_role || formData.job_title,
          department: app.department || formData.department,
          submission_date: app.date || new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
          submission_time: app.time || new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) + ' IST'
        });
        setSubmitted(true);
        
        // Reset form
        setFormData({
          full_name: '',
          email: '',
          phone: '',
          city: '',
          work_location_gurugram: '',
          department: '',
          job_title: '',
          experience_level: '',
          highest_qualification: '',
          employment_status: '',
          current_ctc: '',
          expected_ctc: '',
          compensation_justification: '',
          notice_period: '',
          cover_letter: '',
          declaration_accepted: false,
          website: ''
        });
        setResumeFile(null);
        setResumeFileName('');
        setFieldErrors({});
      }
    } catch (err) {
      console.error('Application submission failed:', err);
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail)
        ? (detail[0]?.msg || 'Please check the form fields and try again.')
        : (typeof detail === 'string' ? detail : 'Application submission failed. Please try again.');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Group roles by department for the dropdown
  const rolesByDepartment = openPositionsData.reduce((acc, pos) => {
    if (!acc[pos.department]) acc[pos.department] = [];
    acc[pos.department].push(pos.title);
    return acc;
  }, {});

  if (submitted && submissionResult) {
    return (
      <div className="page-container" data-testid="careers-success-page">
        <section className="careers-success-portal">
          <div className="container-ultra" style={{ maxWidth: '800px', margin: '0 auto' }}>
            <motion.div 
              className="success-portal-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="success-icon-badge">
                <CheckCircle2 size={56} />
              </div>

              <h1 className="success-heading">Application Successfully Submitted</h1>

              <div className="success-summary-box">
                <div className="summary-grid">
                  <div className="summary-item">
                    <span className="summary-lbl">Application Reference</span>
                    <span className="summary-val app-id-tag">{submissionResult.application_id}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-lbl">Position Applied</span>
                    <span className="summary-val">{submissionResult.job_title}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-lbl">Department</span>
                    <span className="summary-val">{submissionResult.department}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-lbl">Submission Date</span>
                    <span className="summary-val">{submissionResult.submission_date}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-lbl">Submission Time</span>
                    <span className="summary-val">{submissionResult.submission_time}</span>
                  </div>
                </div>
              </div>

              <div className="success-body-message">
                <p>Thank you for applying to <strong>Quanentry Capital Private Limited</strong>.</p>
                <p>Your application has been successfully received by our Human Capital team.</p>
                <p>Our recruitment team will carefully review your credentials. If your background matches our current requirements, we will contact you regarding the next stages of our evaluation process.</p>
                <p className="email-note">An acknowledgement email containing your application reference has been sent to your inbox.</p>
                <p className="email-reminder-text">
                  Please check your Inbox and Spam/Junk folder for your Application Reference Number if you do not receive the email within a few minutes.
                </p>
              </div>

              <div className="success-action-row">
                <button 
                  type="button" 
                  className="btn-primary return-careers-btn"
                  onClick={() => {
                    setSubmitted(false);
                    setSubmissionResult(null);
                    setNotification('');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  data-testid="return-careers-btn"
                >
                  Return to Careers Portal
                  <ArrowRight size={20} />
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page-container" ref={containerRef} data-testid="careers-page">
      {/* Hero Section */}
      <section className="careers-hero-ultra">
        <CareersInstitutionLoopCanvas />
        <motion.div className="careers-cosmic-bg" style={{ y, opacity }}>
          <div className="cosmic-orb orb-mega-1"></div>
          <div className="cosmic-orb orb-mega-2"></div>
          <div className="careers-grid-pattern"></div>
        </motion.div>

        <motion.div 
          className="careers-hero-content"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="hero-badge-careers">
            <Sparkles size={18} className="badge-sparkle" />
            Join India's Elite Quantitative Trading Firm
          </div>

          <h1 className="display-huge gradient-text-careers">
            {careersContent.title}
          </h1>

          <p className="hero-description-careers">
            {careersContent.introduction}
          </p>

          <div className="careers-stats-bar">
            {CAREERS_HERO_STATS.map((stat, i) => (
              <div 
                key={i} 
                className={`careers-stat-item ${stat.targetId ? "clickable" : ""}`}
                onClick={() => {
                  if (stat.targetId) {
                    const el = document.getElementById(stat.targetId);
                    if (el) {
                      el.scrollIntoView({ behavior: "smooth" });
                    }
                  }
                }}
                role={stat.targetId ? "button" : undefined}
                tabIndex={stat.targetId ? 0 : undefined}
                onKeyDown={(e) => {
                  if (stat.targetId && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    const el = document.getElementById(stat.targetId);
                    if (el) {
                      el.scrollIntoView({ behavior: "smooth" });
                    }
                  }
                }}
              >
                <div className="careers-stat-icon">{stat.icon}</div>
                <div>
                  <div className="careers-stat-value">{stat.value}</div>
                  <div className="careers-stat-label">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Culture & Why Join Section */}
      <section className="why-work-ultra">
        <div className="container-ultra">
          <div className="why-work-grid">
            <div className="why-work-content">
              <span className="section-label">Why Quanentry?</span>
              <h2 className="display-medium">{careersContent.whyWorkWithUs.title}</h2>
              <p className="body-large why-text">{careersContent.whyWorkWithUs.content}</p>

              <div className="perks-grid">
                {WHY_WORK_PERKS.map((perk, i) => (
                  <div key={i} className="perk-item">
                    <div className="perk-icon">{perk.icon}</div>
                    <h4>{perk.title}</h4>
                    <p>{perk.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="why-visual">
              <WhyVisualGraphic />
            </div>
          </div>
        </div>
      </section>

      {/* OPEN POSITIONS SECTION */}
      <section className="roles-ultra-section" id="open-positions">
        <div className="container-ultra">
          <div className="section-header-ultra">
            <span className="section-label-center">Career Opportunities</span>
            <h2 className="display-medium">Open Positions ({openPositionsData.length})</h2>
            <p className="section-subtitle">
              Explore available roles across our divisions. Click any position to review full details and apply.
            </p>
          </div>

          {/* Department Filter Tabs & Search Bar */}
          <div className="job-filter-container">
            <div className="job-search-box">
              <Search size={20} className="search-icon" />
              <input
                type="text"
                placeholder="Search by role title, department, or required skill..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="job-search-input"
                data-testid="job-search-input"
              />
              {searchQuery && (
                <button className="clear-search-btn" onClick={handleClearSearch}>Clear</button>
              )}
            </div>

            <DepartmentCarousel
              departments={departments}
              selectedDepartment={selectedDepartment}
              onSelectDepartment={handleSelectDepartment}
            />
          </div>

          {/* Results Counter */}
          <div className="results-counter">
            Showing <strong>{filteredPositions.length}</strong> {filteredPositions.length === 1 ? 'position' : 'positions'}
            {selectedDepartment !== "All" && <span> in <em>{selectedDepartment}</em></span>}
          </div>

          {/* OPEN POSITIONS GRID */}
          <div className="positions-grid-container">
            {filteredPositions.length === 0 ? (
              <div className="no-positions-found" style={{ gridColumn: '1 / -1' }}>
                <AlertCircle size={48} className="no-pos-icon" />
                <h3>No roles match your search</h3>
                <p>Try resetting department filters or search keywords.</p>
                <button className="btn-secondary" onClick={handleResetFilters}>
                  Reset Filters
                </button>
              </div>
            ) : (
              filteredPositions.map((pos) => (
                <JobCardCompact 
                  key={pos.id} 
                  pos={pos} 
                  onViewDetails={handleSelectJobDetails} 
                />
              ))
            )}
          </div>

          {/* SLIDE-OVER PANEL & BACKDROP MODAL */}
          <AnimatePresence>
            {selectedJobDetails && (
              <JobDetailsSlideOver
                selectedJobDetails={selectedJobDetails}
                onClose={handleCloseJobDetails}
                onApply={handleApplyFromSlideOver}
              />
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* APPLICATION FORM SECTION */}
      <section className="apply-ultra-section" id="apply-form" ref={applyFormRef}>
        <div className="container-ultra">
          <div className="section-header-ultra">
            <span className="section-label-center">Candidate Portal</span>
            <h2 className="display-medium">Application Form</h2>
            <p className="section-subtitle">Complete the enterprise recruitment workflow below. All sections marked with an asterisk (*) are mandatory.</p>
          </div>

          <div className="apply-form-container">
            {notification && (
              <motion.div 
                className="selected-role-banner"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <CheckCircle2 size={20} />
                <span>{notification}</span>
                <button onClick={() => setNotification('')} className="close-notif-btn">✕</button>
              </motion.div>
            )}

            <AnimatePresence>
              {error && (
                <motion.div 
                  className="error-message-ultra"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  data-testid="form-error"
                >
                  <AlertCircle size={22} />
                  <span>{error}</span>
                </motion.div>
              )}
            </AnimatePresence>

            <form onSubmit={handleSubmit} className="application-form-institutional" data-testid="application-form">
              {/* Honeypot */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={formData.website || ''}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}
                data-testid="careers-honeypot"
              />

              {/* SECTION 1: PERSONAL INFORMATION */}
              <div className="form-section-card">
                <div className="section-card-header">
                  <div className="section-step-num">01</div>
                  <div>
                    <h3 className="section-card-title">Personal Information</h3>
                    <p className="section-card-sub">Enter your verified contact details for candidate communications.</p>
                  </div>
                </div>
                
                <div className="form-grid-2">
                  {/* Full Name */}
                  <div className="form-group-ultra">
                    <label htmlFor="full_name" className="form-label-ultra">
                      Full Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      className={`form-input-ultra ${fieldErrors.full_name ? 'input-error' : ''}`}
                      placeholder="e.g. Rahul Sharma"
                      data-testid="input-full-name"
                    />
                    {fieldErrors.full_name && <span className="inline-field-error">{fieldErrors.full_name}</span>}
                  </div>

                  {/* Email Address */}
                  <div className="form-group-ultra">
                    <label htmlFor="email" className="form-label-ultra">
                      Email Address <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`form-input-ultra ${fieldErrors.email ? 'input-error' : ''}`}
                      placeholder="e.g. rahul.sharma@example.com"
                      data-testid="input-email"
                    />
                    {fieldErrors.email && <span className="inline-field-error">{fieldErrors.email}</span>}
                  </div>

                  {/* Mobile Number */}
                  <div className="form-group-ultra">
                    <label htmlFor="phone" className="form-label-ultra">
                      Mobile Number <span className="required">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`form-input-ultra ${fieldErrors.phone ? 'input-error' : ''}`}
                      placeholder="e.g. +91 9876543210"
                      data-testid="input-phone"
                    />
                    {fieldErrors.phone && <span className="inline-field-error">{fieldErrors.phone}</span>}
                  </div>

                  {/* Current City & Work Location Preference (Combined Field) */}
                  <div className="form-group-ultra">
                    <label htmlFor="city" className="form-label-ultra">
                      Current City & Gurugram Office Preference <span className="required">*</span>
                    </label>
                    <div className="combined-city-office-input">
                      <div className="flex-city-input-col">
                        <input
                          type="text"
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          className={`form-input-ultra ${fieldErrors.city ? 'input-error' : ''}`}
                          placeholder="Current City (e.g. Gurugram, Delhi NCR)"
                          data-testid="input-city"
                        />
                        {fieldErrors.city && <span className="inline-field-error">{fieldErrors.city}</span>}
                      </div>
                      <div className="fixed-office-select-col">
                        <select
                          id="work_location_gurugram"
                          name="work_location_gurugram"
                          value={formData.work_location_gurugram}
                          onChange={handleInputChange}
                          className={`form-select-ultra ${fieldErrors.work_location_gurugram ? 'input-error' : ''}`}
                          data-testid="select-work-location-gurugram"
                        >
                          <option value="">-- Select --</option>
                          {gurugramWorkOptions.map((opt, i) => (
                            <option key={i} value={opt}>{opt}</option>
                          ))}
                        </select>
                        {fieldErrors.work_location_gurugram && <span className="inline-field-error">{fieldErrors.work_location_gurugram}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION 2: POSITION DETAILS */}
              <div className="form-section-card">
                <div className="section-card-header">
                  <div className="section-step-num">02</div>
                  <div>
                    <h3 className="section-card-title">Position Details</h3>
                    <p className="section-card-sub">Select the division and target position you wish to apply for.</p>
                  </div>
                </div>

                <div className="form-grid-2">
                  {/* Department Dropdown */}
                  <div className="form-group-ultra">
                    <label htmlFor="department" className="form-label-ultra">
                      Department <span className="required">*</span>
                    </label>
                    <select
                      id="department"
                      name="department"
                      value={formData.department}
                      onChange={handleDepartmentChange}
                      className={`form-select-ultra ${fieldErrors.department ? 'input-error' : ''}`}
                      data-testid="select-department"
                    >
                      <option value="">-- Select Department --</option>
                      {availableDepartments.map((dept, i) => (
                        <option key={i} value={dept}>{dept}</option>
                      ))}
                    </select>
                    {fieldErrors.department && <span className="inline-field-error">{fieldErrors.department}</span>}
                  </div>

                  {/* Job Title Dropdown - Filtered automatically based on selected Department */}
                  <div className="form-group-ultra">
                    <label htmlFor="job_title" className="form-label-ultra">
                      Job Title <span className="required">*</span>
                    </label>
                    <select
                      id="job_title"
                      name="job_title"
                      value={formData.job_title}
                      onChange={handleInputChange}
                      className={`form-select-ultra ${fieldErrors.job_title ? 'input-error' : ''}`}
                      disabled={!formData.department}
                      data-testid="select-job-title"
                    >
                      <option value="">
                        {!formData.department ? '-- Select Department First --' : '-- Select Job Title --'}
                      </option>
                      {availableJobTitles.map((roleTitle, i) => (
                        <option key={i} value={roleTitle}>{roleTitle}</option>
                      ))}
                    </select>
                    {fieldErrors.job_title && <span className="inline-field-error">{fieldErrors.job_title}</span>}
                  </div>
                </div>
              </div>

              {/* SECTION 3: PROFESSIONAL INFORMATION */}
              <div className="form-section-card">
                <div className="section-card-header">
                  <div className="section-step-num">03</div>
                  <div>
                    <h3 className="section-card-title">Professional Information</h3>
                    <p className="section-card-sub">Provide details regarding your experience level and highest academic degree.</p>
                  </div>
                </div>

                <div className="form-grid-3">
                  {/* Total Professional Experience */}
                  <div className="form-group-ultra">
                    <label htmlFor="experience_level" className="form-label-ultra">
                      Total Professional Experience <span className="required">*</span>
                    </label>
                    <select
                      id="experience_level"
                      name="experience_level"
                      value={formData.experience_level}
                      onChange={handleInputChange}
                      className={`form-select-ultra ${fieldErrors.experience_level ? 'input-error' : ''}`}
                      data-testid="select-experience"
                    >
                      <option value="">-- Select Total Experience --</option>
                      {experienceOptions.map((opt, i) => (
                        <option key={i} value={opt}>{opt}</option>
                      ))}
                    </select>
                    {fieldErrors.experience_level && <span className="inline-field-error">{fieldErrors.experience_level}</span>}
                  </div>

                  {/* Highest Qualification */}
                  <div className="form-group-ultra">
                    <label htmlFor="highest_qualification" className="form-label-ultra">
                      Highest Qualification <span className="required">*</span>
                    </label>
                    <select
                      id="highest_qualification"
                      name="highest_qualification"
                      value={formData.highest_qualification}
                      onChange={handleInputChange}
                      className={`form-select-ultra ${fieldErrors.highest_qualification ? 'input-error' : ''}`}
                      data-testid="select-qualification"
                    >
                      <option value="">-- Select Highest Qualification --</option>
                      {qualificationOptions.map((q, i) => (
                        <option key={i} value={q}>{q}</option>
                      ))}
                    </select>
                    {fieldErrors.highest_qualification && <span className="inline-field-error">{fieldErrors.highest_qualification}</span>}
                  </div>

                  {/* Current Employment Status */}
                  <div className="form-group-ultra">
                    <label htmlFor="employment_status" className="form-label-ultra">
                      Current Employment Status <span className="required">*</span>
                    </label>
                    <select
                      id="employment_status"
                      name="employment_status"
                      value={formData.employment_status}
                      onChange={handleInputChange}
                      className={`form-select-ultra ${fieldErrors.employment_status ? 'input-error' : ''}`}
                      data-testid="select-employment-status"
                    >
                      <option value="">-- Select Employment Status --</option>
                      {employmentStatusOptions.map((status, i) => (
                        <option key={i} value={status}>{status}</option>
                      ))}
                    </select>
                    {fieldErrors.employment_status && <span className="inline-field-error">{fieldErrors.employment_status}</span>}
                  </div>
                </div>
              </div>

              {/* SECTION 4: COMPENSATION DETAILS */}
              <div className="form-section-card">
                <div className="section-card-header">
                  <div className="section-step-num">04</div>
                  <div>
                    <h3 className="section-card-title">Compensation & Notice Period</h3>
                    <p className="section-card-sub">Provide your current CTC, expected CTC, and notice period availability.</p>
                  </div>
                </div>

                <div className="form-grid-3">
                  {/* Current CTC */}
                  <div className="form-group-ultra">
                    <label htmlFor="current_ctc" className="form-label-ultra">
                      <span>Current CTC (INR / Annum)</span> <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      id="current_ctc"
                      name="current_ctc"
                      value={formData.current_ctc}
                      onChange={handleInputChange}
                      className={`form-input-ultra ${fieldErrors.current_ctc ? 'input-error' : ''}`}
                      placeholder="e.g. 2500000"
                      data-testid="input-current-ctc"
                      required
                    />
                    {fieldErrors.current_ctc && <span className="inline-field-error">{fieldErrors.current_ctc}</span>}
                  </div>

                  {/* Expected CTC */}
                  <div className="form-group-ultra">
                    <label htmlFor="expected_ctc" className="form-label-ultra">
                      <span>Expected CTC (INR / Annum)</span> <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      id="expected_ctc"
                      name="expected_ctc"
                      value={formData.expected_ctc}
                      onChange={handleInputChange}
                      className={`form-input-ultra ${fieldErrors.expected_ctc ? 'input-error' : ''}`}
                      placeholder="e.g. 3500000"
                      data-testid="input-expected-ctc"
                      required
                    />
                    {fieldErrors.expected_ctc && <span className="inline-field-error">{fieldErrors.expected_ctc}</span>}
                  </div>

                  {/* Notice Period */}
                  <div className="form-group-ultra">
                    <label htmlFor="notice_period" className="form-label-ultra">
                      Notice Period <span className="required">*</span>
                    </label>
                    <select
                      id="notice_period"
                      name="notice_period"
                      value={formData.notice_period}
                      onChange={handleInputChange}
                      className={`form-select-ultra ${fieldErrors.notice_period ? 'input-error' : ''}`}
                      data-testid="select-notice-period"
                    >
                      <option value="">-- Select Notice Period --</option>
                      {noticePeriodOptions.map((np, i) => (
                        <option key={i} value={np}>{np}</option>
                      ))}
                    </select>
                    {fieldErrors.notice_period && <span className="inline-field-error">{fieldErrors.notice_period}</span>}
                  </div>
                </div>

                {/* EXPECTED COMPENSATION INCREASE BADGE (Appears only when increase > 100%) */}
                <AnimatePresence>
                  {ctcIncreasePercent > 100 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0, marginTop: 0 }}
                      animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
                      exit={{ opacity: 0, height: 0, marginTop: 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                      style={{ overflow: 'hidden' }}
                    >
                      <div className="compensation-calc-badge" data-testid="compensation-calc-badge">
                        <TrendingUp className="w-3.5 h-3.5" />
                        <span>Compensation Growth Expectation: +{ctcIncreasePercent % 1 === 0 ? ctcIncreasePercent.toFixed(0) : ctcIncreasePercent.toFixed(1)}%</span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* SECTION 5: DOCUMENTS */}
              <div className="form-section-card">
                <div className="section-card-header">
                  <div className="section-step-num">05</div>
                  <div>
                    <h3 className="section-card-title">Documents & Statement of Intent</h3>
                    <p className="section-card-sub">Upload your PDF resume (Max 5 MB) and write your cover letter.</p>
                  </div>
                </div>

                {/* Resume File Upload */}
                <div className="form-group-ultra full-width margin-bottom-24" id="resume">
                  <label className="form-label-ultra">
                    Resume Upload (PDF Only, Max 5 MB) <span className="required">*</span>
                  </label>
                  <div className="file-upload-institutional">
                    <label htmlFor="resume_file" className={`file-upload-dropzone ${fieldErrors.resume ? 'input-error' : ''}`}>
                      <Upload size={24} className="upload-icon" />
                      <div className="upload-text">
                        <span className="upload-title">{resumeFileName ? resumeFileName : 'Click or Drag & Drop PDF Resume'}</span>
                        <span className="upload-sub">PDF format only &bull; Maximum file size 5 MB</span>
                      </div>
                    </label>
                    <input
                      type="file"
                      id="resume_file"
                      name="resume"
                      accept=".pdf"
                      onChange={handleFileChange}
                      className="file-input-hidden"
                      data-testid="input-resume"
                    />
                  </div>
                  {fieldErrors.resume && <span className="inline-field-error">{fieldErrors.resume}</span>}
                  {resumeFileName && (
                    <p className="file-success-badge"><Check size={16} /> Attached Resume: <strong>{resumeFileName}</strong></p>
                  )}
                </div>

                {/* Cover Letter */}
                <div className="form-group-ultra full-width" style={{ marginTop: '20px' }}>
                  <label htmlFor="cover_letter" className="form-label-ultra">
                    Cover Letter / Statement of Intent <span className="required">*</span>
                  </label>
                  <textarea
                    id="cover_letter"
                    name="cover_letter"
                    value={formData.cover_letter}
                    onChange={handleInputChange}
                    className={`form-textarea-ultra ${fieldErrors.cover_letter ? 'input-error' : ''}`}
                    placeholder="Your Statement of Intent should help us understand your professional background, strengths, and how you believe you can contribute to Quanentry Capital. Briefly describe your most relevant experience, notable achievements, strongest technical, analytical, or functional skills, how your expertise can create value within the role and contribute to Quanentry Capital's long-term success, and why you are interested in joining the firm at this stage of your career. You may also include any additional information that you believe would assist our Human Capital team during the evaluation of your application."
                    rows={8}
                    data-testid="input-cover-letter"
                  />
                  <div className="char-counter">
                    {formData.cover_letter.trim().length} / 4000 characters (Min 100 required)
                  </div>
                  {fieldErrors.cover_letter && <span className="inline-field-error">{fieldErrors.cover_letter}</span>}
                </div>
              </div>

              {/* SECTION 6: DECLARATION */}
              <div className="form-section-card declaration-section">
                <div className="section-card-header">
                  <div className="section-step-num">06</div>
                  <div>
                    <h3 className="section-card-title">Candidate Declaration & Terms</h3>
                    <p className="section-card-sub">Review and accept the recruitment terms before submitting your application.</p>
                  </div>
                </div>

                <div className="declaration-checkbox-wrapper">
                  <label className="checkbox-institutional" htmlFor="declaration_accepted_checkbox">
                    <div className="custom-checkbox-container">
                      <input
                        type="checkbox"
                        id="declaration_accepted_checkbox"
                        name="declaration_accepted"
                        checked={formData.declaration_accepted}
                        onChange={handleInputChange}
                        className="checkbox-input-native"
                        data-testid="checkbox-declaration"
                      />
                      <div className={`checkbox-box-custom ${formData.declaration_accepted ? 'checked' : ''} ${fieldErrors.declaration_accepted ? 'input-error' : ''}`}>
                        {formData.declaration_accepted && (
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        )}
                      </div>
                    </div>
                    <span className="checkbox-statement">
                      I confirm that all information submitted in this application is accurate, true, and belongs to me. I authorise Quanentry Capital Private Limited to process my application solely for recruitment purposes. I have read and agree to the Privacy Policy and Recruitment Terms. <span className="required">*</span>
                    </span>
                  </label>
                  {fieldErrors.declaration_accepted && <span className="inline-field-error" style={{ marginTop: '8px' }}>{fieldErrors.declaration_accepted}</span>}
                </div>
              </div>

              {/* SUBMIT BUTTON */}
              <div className="submit-container-institutional">
                {!formData.declaration_accepted && (
                  <div className="declaration-hint-text">
                    <AlertCircle size={14} style={{ display: 'inline-block', flexShrink: 0 }} />
                    <span>Please accept the candidate declaration checkbox above to activate the submit button.</span>
                  </div>
                )}
                <button
                  type="submit"
                  className={`submit-btn-institutional ${formData.declaration_accepted ? 'active' : ''}`}
                  disabled={loading || !formData.declaration_accepted}
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <>
                      <span className="loading-spinner"></span>
                      Submitting Application...
                    </>
                  ) : (
                    <>
                      Submit Application
                      <ArrowRight size={22} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="careers-cta-section">
        <div className="container-ultra text-center">
          <h2 className="display-medium">Have Questions for Our Talent Team?</h2>
          <p className="body-large cta-text">
            Reach out directly for candidate inquiries or general recruitment questions.
          </p>
          <a href={`mailto:${companyInfo.careersEmail}`} className="btn-secondary btn-mega">
            Contact Recruitment: {companyInfo.careersEmail}
            <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default CareersUltra;
